import { useState } from 'react';
import { Link } from 'react-router-dom';
import { discussions, topics } from '@/mocks/topics';
import { formatCount } from '@/utils/format';

const topicMap = new Map(topics.map((topic) => [topic.id, topic]));
const tagCloud = ['剪辑争议', '官方回应', '时间线', '版权', '隐私边界', '退票', '抽检'];

function FeedItem({ commentId }: { commentId: number }) {
  const comment = discussions.find((item) => item.id === commentId);
  const [liked, setLiked] = useState(false);

  if (!comment) return null;
  const topic = topicMap.get(comment.topicId);

  return (
    <li className="border border-background-200 bg-background-50 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center bg-accent-100 font-heading text-sm font-bold text-accent-900">
          {comment.user.slice(0, 1)}
        </span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-foreground-900">{comment.user}</span>
          <span className="block text-xs text-foreground-500">{comment.role}</span>
        </span>
        <span className="text-xs text-foreground-400">{comment.time}</span>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comment.content}</p>

      {topic && (
        <Link
          to={`/topic/${topic.id}`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-secondary-700 transition-colors hover:text-primary-600"
        >
          <i className="ri-discuss-line" aria-hidden="true" />
          来自话题：{topic.title}
        </Link>
      )}

      <div className="mt-3 flex items-center gap-4 border-t border-background-200 pt-3">
        <button
          type="button"
          onClick={() => setLiked((v) => !v)}
          aria-pressed={liked}
          className={`flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
            liked ? 'text-primary-600' : 'text-foreground-500 hover:text-primary-600'
          }`}
        >
          <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true" />
          {formatCount(comment.likes + (liked ? 1 : 0))}
        </button>
        {topic && (
          <Link to={`/topic/${topic.id}`} className="flex items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-primary-600">
            <i className="ri-chat-3-line" aria-hidden="true" />
            参与讨论
          </Link>
        )}
      </div>
    </li>
  );
}

export default function DiscussionFeedSection() {
  const feed = discussions.filter((item) => item.featured).slice(0, 6);
  const ranking = [...topics].sort((a, b) => b.heat - a.heat).slice(0, 6);

  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="w-full px-4 py-14 md:px-8 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600">
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-primary-500" aria-hidden="true" />
              Live Discussion
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              讨论区精选
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground-600">
            从讨论区里挑出的几条分析，有反驳也有补充。观点归观点，证据归证据。
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <ul className="flex flex-col gap-4 lg:col-span-8">
            {feed.map((comment) => (
              <FeedItem key={comment.id} commentId={comment.id} />
            ))}
          </ul>

          <aside className="lg:col-span-4">
            <div className="border border-background-200 bg-background-50">
              <h3 className="border-b border-background-200 px-5 py-4 font-heading text-sm font-bold text-foreground-950">
                今日热榜 TOP 6
              </h3>
              <ol className="divide-y divide-background-200">
                {ranking.map((topic, index) => (
                  <li key={topic.id}>
                    <Link to={`/topic/${topic.id}`} className="flex items-start gap-3 px-5 py-3.5 transition-colors hover:bg-background-100">
                      <span
                        className={`mt-0.5 font-heading text-sm font-bold ${
                          index < 3 ? 'text-primary-500' : 'text-foreground-400'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">
                        <span className="line-clamp-2 text-sm text-foreground-800">{topic.title}</span>
                        <span className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                          <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                          {formatCount(topic.heat)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-6 border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950">热门标签</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {tagCloud.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="cursor-pointer bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}